import React, { useEffect, useState } from "react";
import { Trash2, Mail, Loader2, Inbox, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { PageHeader } from "./AdminUI";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const MessagesAdmin = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await api.listMessages());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onOpen = async (m) => {
    setOpenId((cur) => (cur === m.id ? null : m.id));
    if (!m.read) {
      try {
        await api.markMessageRead(m.id);
        setItems((arr) => arr.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
      } catch (e) {
        // ignore
      }
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.deleteMessage(id);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast({ title: "Deleted" });
    } catch (e) {
      toast({ title: "Couldn't delete", description: e.message });
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline-block text-[color:var(--muted)]" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader label="Inbox" title={`Messages (${items.length})`} />

      {items.length === 0 && (
        <div className="py-20 text-center border border-dashed border-black/15 rounded-2xl">
          <Inbox className="w-8 h-8 mx-auto text-[color:var(--muted)]" />
          <div className="mt-3 text-[color:var(--muted)]">No messages yet.</div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((m) => {
          const isOpen = openId === m.id;
          return (
            <div key={m.id} className="rounded-2xl bg-white border border-black/10 overflow-hidden">
              <button
                onClick={() => onOpen(m)}
                className="w-full text-left flex items-center gap-4 p-4 md:p-5 hover:bg-black/[.02] transition-colors"
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full ${m.read ? "bg-black/20" : "bg-[color:var(--coral)]"}`}
                  aria-hidden
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-display text-lg font-semibold truncate">{m.name}</span>
                    <span className="text-xs text-[color:var(--muted)] truncate">{m.email}</span>
                    {m.service && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--coral-soft)]">
                        {m.service}
                      </span>
                    )}
                    {m.budget && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--mint)]">
                        {m.budget}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-[color:var(--ink-soft)] truncate">
                    {m.message}
                  </div>
                </div>
                <div className="text-xs text-[color:var(--muted)] hidden sm:block">
                  {formatDate(m.created_at)}
                </div>
              </button>
              {isOpen && (
                <div className="px-4 md:px-5 pb-5 border-t border-black/10">
                  <div className="mt-4 whitespace-pre-wrap text-[color:var(--ink-soft)] leading-relaxed">
                    {m.message}
                  </div>
                  <div className="mt-5 flex flex-wrap gap-3 items-center justify-between">
                    <div className="text-xs text-[color:var(--muted)]">{formatDate(m.created_at)}</div>
                    <div className="flex gap-2">
                      <a
                        href={`mailto:${m.email}?subject=Re: your project`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/20 text-sm hover:border-black/50"
                      >
                        <Mail className="w-4 h-4" /> Reply
                      </a>
                      <button
                        onClick={() => onDelete(m.id)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/20 text-sm hover:border-black/50"
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MessagesAdmin;
